import React, { useState, useEffect, useRef } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import CopyrightIcon from '@mui/icons-material/Copyright';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import '../shared.css';
import LoadingOverlay from '../LoadingOverlay';

function BestSellerSales() {
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();

    const [totalSales, setTotalSales] = useState(0);
    const [totalNexusHydSale, setTotalNexusHydSale] = useState(0);
    const [totalInorbitSale, setTotalInorbitSale] = useState(0);
    const [totalLTSale, setTotalLTSale] = useState(0);
    const [totalOrionSale, setTotalOrionSale] = useState(0);
    const [totalPMCBLRSale, setTotalPMCBLRSale] = useState(0);
    const [totalShantiniketanSale, setTotalShantiniketanSale] = useState(0);
    const [totalUBCitySale, setTotalUBCitySale] = useState(0);
    const [totalVRSale, setTotalVRSale] = useState(0);
    const [totalPMCCHNSale, setTotalPMCCHNSale] = useState(0);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        const startDate = getFormattedStartDate();
        const currentDate = getFormattedCurrentDate();
        setFromDate(startDate);
        setToDate(currentDate);
    }, [isAuthenticated, companyId, branchId, token, logout]);

    useEffect(() => {
        if (fromDate && toDate) {
            fetchTableData(fromDate, toDate);
        }
    }, [fromDate, toDate]);

    const [totalbills, setTotalbills] = useState(0);

    useEffect(() => {
        let totalSales = 0, totalNexusHydSale = 0, totalInorbitSale = 0, totalLTSale = 0;
        let totalOrionSale = 0, totalPMCBLRSale = 0, totalShantiniketanSale = 0;
        let totalUBCitySale = 0, totalVRSale = 0, totalPMCCHNSale = 0;

        dataItem.forEach(item => {
            totalSales += item.TotalSales;
            totalNexusHydSale += item.NexusHydSale;
            totalInorbitSale += item.InorbitSale;
            totalLTSale += item.LTSale;
            totalOrionSale += item.OrionSale;
            totalPMCBLRSale += item.PMCBLRSale;
            totalShantiniketanSale += item.ShantiniketanSale;
            totalUBCitySale += item.UBCitySale;
            totalVRSale += item.VRSale;
            totalPMCCHNSale += item.PMCCHNSale;
        });

        setTotalSales(totalSales);
        setTotalNexusHydSale(totalNexusHydSale);
        setTotalInorbitSale(totalInorbitSale);
        setTotalLTSale(totalLTSale);
        setTotalOrionSale(totalOrionSale);
        setTotalPMCBLRSale(totalPMCBLRSale);
        setTotalShantiniketanSale(totalShantiniketanSale);
        setTotalUBCitySale(totalUBCitySale);
        setTotalVRSale(totalVRSale);
        setTotalPMCCHNSale(totalPMCCHNSale);
    }, [dataItem]);

    const fetchCustomerData = () => {};

    const getFormattedCurrentDate = () => new Date().toISOString().split('T')[0];

    const getFormattedStartDate = () => {
        const d = new Date();
        d.setDate(1);
        return d.toISOString().split('T')[0];
    };

    const handleFromDateChange = (e) => setFromDate(e.target.value);
    const handleToDateChange = (e) => setToDate(e.target.value);

    const hasReadReportPermission = permissions.includes('Read Reports');
    const [loading, setLoading] = useState(false);
    const loadingRef = useRef(false);

    const fetchTableData = async (fDate, tDate) => {
        const effectiveFromDate = fDate || fromDate;
        const effectiveToDate = tDate || toDate;
        if (hasReadReportPermission && !loadingRef.current && effectiveFromDate && effectiveToDate) {
            setDataItem([]);
            loadingRef.current = true;
            setLoading(true);
            const branchId1 = 0;
            try {
                const response = await fetchData('ReportApi', `/BestSellerSales/${companyId}/${branchId1}/${effectiveFromDate}/${effectiveToDate}`, token);
                if (response.responseCode === '1111') {
                    setDataItem(response.response_data);
                }
            } catch (error) {
                console.error('Error fetching Daily Sales Summary Details:', error);
            } finally {
                loadingRef.current = false;
                setLoading(false);
            }
        }
    };

    const handleSearch = (event) => setSearchQuery(event.target.value);

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();

        const categoryDataMap = {};
        filteredDataItem.forEach((rowData) => {
            const category = (rowData.ItemCategory || 'Unnamed').toLowerCase();
            if (!categoryDataMap[category]) {
                categoryDataMap[category] = [];
            }
            categoryDataMap[category].push(rowData);
        });

        createSheetData(workbook, filteredDataItem, 'All Data', true);

        Object.keys(categoryDataMap).forEach((category) => {
            const sheetName = category.charAt(0).toUpperCase() + category.slice(1);
            createSheetData(workbook, categoryDataMap[category], sheetName, true);
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), 'BestSeller Sales Report.xlsx');
    };

    const createSheetData = (workbook, dataItems, sheetName, includeTotal) => {
        const worksheet = workbook.addWorksheet(sheetName);

        const rightAlignColumns = [
            'MRP', 'Discount', 'Total Portfolio Sales', 'Nexus Hyd Sale',
            'Inorbit Sale', 'L&T Sale', 'Orion Sale', 'PMC BLR Sale',
            'Shantiniketan Sale', 'UB City Sale', 'VR Sale', 'PMC CHN Sale'
        ];

        const headerCaption = `Best Seller Sales Report For: ${sheetName} From: ${formatDate(fromDate)} To: ${formatDate(toDate)}`;
        worksheet.mergeCells('A1:R1');
        const captionCell = worksheet.getCell('A1');
        captionCell.value = headerCaption;
        captionCell.font = { bold: true, size: 14 };
        captionCell.alignment = { vertical: 'middle', horizontal: 'left' };

        const headerRowValues = [
            'Item Code', 'Item Name', 'MRP', 'Category', 'Group', 'Season Code', 'Size Code',
            'Discount', 'Total Portfolio Sales', 'Nexus Hyd Sale',
            'Inorbit Sale', 'L&T Sale', 'Orion Sale', 'PMC BLR Sale',
            'Shantiniketan Sale', 'UB City Sale', 'VR Sale', 'PMC CHN Sale'
        ];
        const headerRow = worksheet.addRow(headerRowValues);
        headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB6D7A8' } };
            cell.font = { bold: true, color: { argb: 'FF000000' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });

        worksheet.columns = headerRowValues.map(() => ({ width: 18 }));

        let totals = {
            totalSales: 0, totalNexusHydSale: 0, totalInorbitSale: 0, totalLTSale: 0,
            totalOrionSale: 0, totalPMCBLRSale: 0, totalShantiniketanSale: 0,
            totalUBCitySale: 0, totalVRSale: 0, totalPMCCHNSale: 0
        };

        dataItems.forEach((rowData) => {
            const rowValues = [
                rowData.ItemCode, rowData.ItemName, parseFloat(rowData.MRP),
                rowData.CategoryName, rowData.GroupName, rowData.SeasonCode, rowData.SizeCode,
                parseFloat(rowData.Discount), parseFloat(rowData.TotalSales),
                parseFloat(rowData.NexusHydSale), parseFloat(rowData.InorbitSale),
                parseFloat(rowData.LTSale), parseFloat(rowData.OrionSale),
                parseFloat(rowData.PMCBLRSale), parseFloat(rowData.ShantiniketanSale),
                parseFloat(rowData.UBCitySale), parseFloat(rowData.VRSale),
                parseFloat(rowData.PMCCHNSale)
            ];
            const row = worksheet.addRow(rowValues);
            row.eachCell((cell, colNumber) => {
                const headerName = headerRowValues[colNumber - 1];
                if (rightAlignColumns.includes(headerName)) {
                    cell.alignment = { vertical: 'middle', horizontal: 'right' };
                    cell.numFmt = '#,##0.00';
                } else {
                    cell.alignment = { vertical: 'middle', horizontal: 'center' };
                }
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });

            totals.totalSales += rowData.TotalSales;
            totals.totalNexusHydSale += rowData.NexusHydSale;
            totals.totalInorbitSale += rowData.InorbitSale;
            totals.totalLTSale += rowData.LTSale;
            totals.totalOrionSale += rowData.OrionSale;
            totals.totalPMCBLRSale += rowData.PMCBLRSale;
            totals.totalShantiniketanSale += rowData.ShantiniketanSale;
            totals.totalUBCitySale += rowData.UBCitySale;
            totals.totalVRSale += rowData.VRSale;
            totals.totalPMCCHNSale += rowData.PMCCHNSale;
        });

        if (includeTotal) {
            const totalRowValues = [
                '', '', '', '', '', '', '', 'Total',
                totals.totalSales, totals.totalNexusHydSale, totals.totalInorbitSale,
                totals.totalLTSale, totals.totalOrionSale, totals.totalPMCBLRSale,
                totals.totalShantiniketanSale, totals.totalUBCitySale,
                totals.totalVRSale, totals.totalPMCCHNSale
            ];
            const totalRow = worksheet.addRow(totalRowValues);
            totalRow.font = { bold: true };
            totalRow.eachCell((cell, colNumber) => {
                const headerName = headerRowValues[colNumber - 1];
                if (rightAlignColumns.includes(headerName)) {
                    cell.alignment = { vertical: 'middle', horizontal: 'right' };
                    cell.numFmt = '#,##0.00';
                } else {
                    cell.alignment = { vertical: 'middle', horizontal: 'center' };
                }
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        }

        return worksheet;
    };

    return (
        <div className="pg-container">
            <div className="pg-header">
                <h5 className="pg-title">Best Seller Sales Report</h5>
                <div className="pg-header-actions">
                    <div className="pg-date-row">
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>From Date</label>
                            <input className="pg-input" type="date" value={fromDate} onChange={handleFromDateChange} />
                        </div>
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>To Date</label>
                            <input className="pg-input" type="date" value={toDate} onChange={handleToDateChange} />
                        </div>
                        <button className="pg-btn pg-btn-outline" onClick={() => fetchTableData()} disabled={loading}>
                            <RefreshIcon /> {loading ? 'Loading...' : 'Refresh'}
                        </button>
                        <button className="pg-btn pg-btn-success" onClick={handleExportExcel}>
                            <FeedIcon /> Excel
                        </button>
                        <button className="pg-btn pg-btn-outline" onClick={() => setShowSearchInput(v => !v)}>
                            <SearchIcon /> Search
                        </button>
                    </div>
                    {showSearchInput && (
                        <input className="pg-input pg-search-input" placeholder="Search…"
                            value={searchQuery} onChange={handleSearch} autoFocus style={{ marginTop: 6 }} />
                    )}
                </div>
            </div>

            {loading && <LoadingOverlay />}

            <div className="pg-table-wrap">
                <table className="pg-table">
                    <thead>
                        <tr>
                            <th>Item Code</th>
                            <th>Item Name</th>
                            <th>MRP</th>
                            <th>Category</th>
                            <th>Group</th>
                            <th>Season Code</th>
                            <th>Size Code</th>
                            <th>Discount</th>
                            <th>Total Portfolio Sales</th>
                            <th>Nexus Hyd Sale</th>
                            <th>Inorbit Sale</th>
                            <th>L&T Sale</th>
                            <th>Orion Sale</th>
                            <th>PMC BLR Sale</th>
                            <th>Shantiniketan Sale</th>
                            <th>UB City Sale</th>
                            <th>VR Sale</th>
                            <th>PMC CHN Sale</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredDataItem.map((data, index) => (
                            <tr key={index}>
                                <td>{data.ItemCode}</td>
                                <td>{data.ItemName}</td>
                                <td>{data.MRP}</td>
                                <td>{data.CategoryName}</td>
                                <td>{data.ItemCategory}</td>
                                <td>{data.SeasonCode}</td>
                                <td>{data.SizeCode}</td>
                                <td>{data.Discount}</td>
                                <td>{data.TotalSales}</td>
                                <td>{data.NexusHydSale}</td>
                                <td>{data.InorbitSale}</td>
                                <td>{data.LTSale}</td>
                                <td>{data.OrionSale}</td>
                                <td>{data.PMCBLRSale}</td>
                                <td>{data.ShantiniketanSale}</td>
                                <td>{data.UBCitySale}</td>
                                <td>{data.VRSale}</td>
                                <td>{data.PMCCHNSale}</td>
                            </tr>
                        ))}
                        <tr style={{ background: '#1c2a3a', color: '#fff', fontWeight: 700 }}>
                            <td colSpan={7}></td>
                            <td>Total</td>
                            <td>{totalSales}</td>
                            <td>{totalNexusHydSale}</td>
                            <td>{totalInorbitSale}</td>
                            <td>{totalLTSale}</td>
                            <td>{totalOrionSale}</td>
                            <td>{totalPMCBLRSale}</td>
                            <td>{totalShantiniketanSale}</td>
                            <td>{totalUBCitySale}</td>
                            <td>{totalVRSale}</td>
                            <td>{totalPMCCHNSale}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
        </div>
    );
}

export default BestSellerSales;
